<template>
  <div id="checkout" :class="checkoutClasses">
    <div class="container">
      <div class="row" v-show="!isThankYouPage">
        <div class="col-sm-12 col-xs-12 col-md-7 pb70 left-side-column">
          <div class="checkout-title">
            <div class="flex16">
              <div class="checkout-logo" />
              <div class="checkout-heading">
                <h2 class="cl-accent">
                  {{ $t(checkoutTitle) }}
                </h2>
                <h5 v-if="showProductPartnerName && partnerName">
                  {{ $t('Partner') }}: {{ partnerName }}
                </h5>
              </div>
            </div>
          </div>

          <component
            v-for="step in renderedSteps"
            :key="step.name"
            :is="step.component"
            :class="step.className"
            :is-active="activeSection[step.activeSection]"
            :focused-field="focusedField"
            :mode="step.mode"
            :required-fields="customerRequiredFields"
            :step-number="step.stepNumber"
            :checkout-config="checkoutConfig"
          />
          <div id="custom-steps" />
        </div>
        <div class="hidden-xs col-sm-5 bg-cl-secondary">
          <cart-summary :mode="cartSummaryMode" />
        </div>
      </div>
    </div>
    <thank-you-page v-show="isThankYouPage" />
  </div>
</template>

<script>
import Checkout from '@vue-storefront/core/pages/Checkout'
import { registerModule } from '@vue-storefront/core/lib/modules'
import { OrderModule } from '@vue-storefront/core/modules/order'
import { mapGetters, mapState } from 'vuex'

import AdditionalServices from 'theme/components/core/blocks/Checkout/AdditionalServices'
import BiometricAdditionalServices from 'theme/components/core/blocks/Checkout/SBCR/AdditionalServices.vue'
import CartSummary from 'theme/components/core/blocks/Checkout/CartSummary'
import OrderReview from 'theme/components/core/blocks/Checkout/OrderReview'
import Payment from 'theme/components/core/blocks/Checkout/Payment'
import PersonalDetails from 'theme/components/core/blocks/Checkout/PersonalDetails'
import ReturnShipping from 'theme/components/core/blocks/Checkout/ReturnShipping'
import Shipping from 'theme/components/core/blocks/Checkout/Shipping'
import ShippingAndReturn from 'theme/components/core/blocks/Checkout/SBCR/ShippingAndReturn.vue'
import ThankYouPage from 'theme/components/core/blocks/Checkout/ThankYouPage'

const defaultCheckoutConfig = {
  title: 'AnyRent',
  showProductPartnerName: true,
  layout: {
    variant: 'standard'
  },
  steps: ['additionalServices', 'personalDetails', 'shipping', 'returnShipping', 'payment', 'orderReview'],
  customer: {
    requiredFields: ['firstName', 'lastName', 'emailAddress']
  },
  shipping: {
    mode: 'separate'
  },
  payment: {
    mode: 'fullBilling'
  },
  additionalServices: {
    mode: 'standard'
  },
  cartSummary: {
    mode: 'standard'
  },
  consents: {
    terms: { required: true, slug: 'obchodni-podminky' }
  }
}

const stepDefinitions = {
  additionalServices: {
    activeSection: 'additionalServices',
    className: 'relative',
    getComponent: config => config.additionalServices?.mode === 'biometric' ? 'biometric-additional-services' : 'additional-services',
    getMode: config => config.additionalServices?.mode || 'standard'
  },
  personalDetails: {
    activeSection: 'personalDetails',
    className: 'relative',
    getComponent: () => 'personal-details'
  },
  shipping: {
    activeSection: 'shipping',
    className: 'relative cl-accent',
    getComponent: () => 'shipping'
  },
  returnShipping: {
    activeSection: 'returnShipping',
    className: 'relative cl-accent',
    getComponent: () => 'return-shipping'
  },
  shippingAndReturn: {
    activeSection: 'shipping',
    className: 'relative cl-accent',
    getComponent: () => 'shipping-and-return'
  },
  payment: {
    activeSection: 'payment',
    className: 'relative cl-accent',
    getComponent: () => 'payment',
    getMode: config => config.payment?.mode || 'fullBilling'
  },
  orderReview: {
    activeSection: 'orderReview',
    className: 'relative cl-accent',
    getComponent: () => 'order-review'
  }
}

export default {
  name: 'ConfiguredCheckout',
  components: {
    AdditionalServices,
    BiometricAdditionalServices,
    CartSummary,
    OrderReview,
    Payment,
    PersonalDetails,
    ReturnShipping,
    Shipping,
    ShippingAndReturn,
    ThankYouPage
  },
  mixins: [Checkout],
  beforeCreate () {
    registerModule(OrderModule)
  },
  computed: {
    ...mapGetters({
      getCurrentProduct: 'product/getCurrentProduct'
    }),
    ...mapState({
      storeView: state => state.storeView
    }),
    checkoutConfig () {
      const storeCheckout = this.storeView && this.storeView.checkout ? this.storeView.checkout : {};
      const checkoutConfig = {
        ...defaultCheckoutConfig,
        ...storeCheckout,
        layout: {
          ...defaultCheckoutConfig.layout,
          ...(storeCheckout.layout || {})
        },
        customer: {
          ...defaultCheckoutConfig.customer,
          ...(storeCheckout.customer || {})
        },
        shipping: {
          ...defaultCheckoutConfig.shipping,
          ...(storeCheckout.shipping || {})
        },
        payment: {
          ...defaultCheckoutConfig.payment,
          ...(storeCheckout.payment || {})
        },
        additionalServices: {
          ...defaultCheckoutConfig.additionalServices,
          ...(storeCheckout.additionalServices || {})
        },
        cartSummary: {
          ...defaultCheckoutConfig.cartSummary,
          ...(storeCheckout.cartSummary || {})
        },
        consents: {
          ...defaultCheckoutConfig.consents,
          ...(storeCheckout.consents || {})
        }
      }

      return checkoutConfig
    },
    checkoutTitle () {
      return this.checkoutConfig.title || defaultCheckoutConfig.title
    },
    showProductPartnerName () {
      return !!this.checkoutConfig.showProductPartnerName
    },
    partnerName () {
      return this.getCurrentProduct && this.getCurrentProduct.rental_info
        ? this.getCurrentProduct.rental_info.name
        : null
    },
    customerRequiredFields () {
      return this.checkoutConfig.customer?.requiredFields || defaultCheckoutConfig.customer.requiredFields
    },
    cartSummaryMode () {
      return this.checkoutConfig.cartSummary?.mode || 'standard'
    },
    checkoutClasses () {
      return [
        `checkout--${this.checkoutConfig.layout && this.checkoutConfig.layout.variant ? this.checkoutConfig.layout.variant : 'standard'}`,
        `checkout--theme-${this.storeView && this.storeView.theme && this.storeView.theme.name ? this.storeView.theme.name : 'default'}`
      ]
    },
    renderedSteps () {
      const visibleSteps = this.checkoutConfig.steps.filter(step => {
        if (step !== 'additionalServices') {
          return true
        }

        return !(this.isVirtualCart && this.checkoutConfig.layout && this.checkoutConfig.layout.hideAdditionalServicesForVirtualCart)
      })

      return visibleSteps
        .map((step, index) => {
          const definition = stepDefinitions[step]
          if (!definition) {
            return null
          }

          return {
            name: step,
            component: definition.getComponent(this.checkoutConfig),
            activeSection: definition.activeSection,
            className: definition.className,
            mode: definition.getMode ? definition.getMode(this.checkoutConfig) : undefined,
            stepNumber: index + 1
          }
        })
        .filter(Boolean)
    }
  },
  methods: {
    notifyPastDate () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'warning',
        message: this.$t('Your reservation dates are in past. The cart will be reseted, please select new products and dates'),
        action1: { label: this.$t('OK') }
      })
    },
    notifyEmptyCart () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'warning',
        message: this.$t('Shopping cart is empty. Please add some products before entering Checkout'),
        action1: { label: this.$t('OK') }
      })
    },
    notifyOutStock (chp) {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'error',
        message: chp.name + this.$t(' is out of stock!'),
        action1: { label: this.$t('OK') }
      })
    },
    notifyNotAvailable () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'error',
        message: this.$t('Some of the ordered products are not available!'),
        action1: { label: this.$t('OK') }
      })
    },
    notifyStockCheck () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'warning',
        message: this.$t('Stock check in progress, please wait while available stock quantities are checked'),
        action1: { label: this.$t('OK') }
      })
    },
    notifyNoConnection () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'warning',
        message: this.$t('There is no Internet connection. You can still place your order. We will notify you if any of ordered products is not available because we cannot check it right now.'),
        action1: { label: this.$t('OK') }
      })
    }
  }
}
</script>

<style lang="scss">
  @import '~theme/css/base/text';
  @import '~theme/css/variables/colors';
  @import '~theme/css/helpers/functions/color';
  @import '../components/core/blocks/Checkout/checkout.scss';
  $bg-partner: color(partner);

  #checkout {
    .number-circle {
      width: 35px;
      height: 35px;
      margin-left: 8px;

      @media (max-width: 768px) {
        width: 25px;
        height: 25px;
        line-height: 25px;
      }
    }

    .radioStyled {
      display: block;
      position: relative;
      padding-left: 35px;
      margin-bottom: 12px;
      cursor: pointer;
      font-size: 16px;
      line-height: 30px;
      user-select: none;

      input {
        position: absolute;
        opacity: 0;
        cursor: pointer;
      }

      .checkmark {
        position: absolute;
        top: 0;
        left: 0;
        height: 25px;
        width: 25px;
        border-radius: 50%;
        border: 1px solid $bg-partner;

        &:after {
          content: "";
          position: absolute;
          display: none;
          top: 3px;
          left: 3px;
          width: 19px;
          height: 19px;
          border-radius: 50%;
          background: $bg-partner;
        }
      }

      input:checked ~ .checkmark:after {
        display: block;
      }

      &__disabled {
        cursor: default !important;
        pointer-events: none !important;

        .checkmark {
          opacity: 0.5;
        }
      }
    }

    .disabled-filled {
      cursor: default !important;
      pointer-events: none !important;

      .checkmark {
        opacity: 0.5;
      }
    }
  }
</style>

<style lang="scss" scoped>
  @import '~theme/css/base/text';
  @import '~theme/css/variables/colors';
  @import '~theme/css/helpers/functions/color';
  @import '~theme/css/variables/assets';
  @import '~theme/css/helpers/functions/asset';
  $logo-src: asset(square-logo);

  .checkout-title {
    padding-top: 36px;

    @media (max-width: 767px) {
      h2 {
        font-size: 36px;
      }

      h5 {
        font-size: 18px;
      }
    }
  }

  .checkout--concise {
    .checkout-title {
      margin-bottom: 1rem;
    }
  }

  .checkout--standard {
    @media (max-width: 767px) {
      .checkout-title {
        margin-bottom: 25px;
      }
    }
  }

  .checkout-logo {
    display: flex;
    flex-shrink: 0;
    background-image: $logo-src;
    background-repeat: no-repeat;
    background-size: contain;
    width: 95px;
    height: 95px;
  }

  .checkout--theme-highlandsport {
    .checkout-logo {
      background-position: center;
      width: 124px;
      height: 124px;
    }
  }

  .flex16 {
    display: flex;
    gap: 16px;
  }

  @media (min-width: 500px) {
    .left-side-column {
      padding-right: 2rem;
    }
  }

  .checkout-heading {
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;

    * {
      margin: 0;
    }
  }

  .partner-logo {
    width: 100px;
    height: 100px;
  }

  @media (max-width: 500px) {
    .checkout--theme-highlandsport {
      .flex16 {
        flex-direction: column;
      }

      .checkout-heading {
        display: none;
      }

      .checkout-logo {
        align-self: center;
        margin: 0;
        width: 75%;
      }
    }
  }

  @media (max-width: 575px) {
    .checkout--theme-highlandsport {
      .checkout-title {
        text-align: center;
      }
    }

    .checkout-title h2 {
      font-size: 32px;
    }

    .partner-logo {
      margin-left: 20px;
    }

    .xspr20 {
      padding-right: 20px;
    }

    .xspl40 {
      padding-left: 40px;
    }
  }

  @media (max-width: 767px) {
    #checkout:not(.checkout--theme-highlandsport) {
      .container {
        padding: 0 1rem;
      }
    }

    .xspl20 {
      padding-left: 20px;
    }
  }
</style>

<style lang="scss">
  #checkout.checkout--theme-highlandsport {
    .flex16 {
      @media (max-width: 500px) {
        flex-direction: column;

        .checkout-heading {
          display: none;
        }

        .checkout-logo {
          align-self: center;
          margin: 0;
          width: 75%;
        }
      }
    }
  }
</style>
